import Decimal from 'decimal.js';
import { percentualParaTaxa, type AcaoLoteDto, type AplicarEmLote, type EscopoLoteDto } from '@triade/contracts';
import { decimalDigitado } from '@/lib/format';

/**
 * Formulario das acoes em lote -> corpo de POST /precificacao/rascunho/lote. So converte o que foi digitado
 * (percentual -> fracao, texto -> decimal) e checa o formato; nenhum preco, lucro ou arredondamento e calculado aqui.
 */
export type TipoAcaoLote = AcaoLoteDto['tipo'];
export type RegraArredondamento = Extract<AcaoLoteDto, { tipo: 'ARREDONDAR' }>['regra'];

export interface FormLote {
  tipo: TipoAcaoLote;
  /** Lucro desejado em percentual, como digitado ("20", "17,5"). */
  lucro: string;
  /** Preco como digitado ("25,00"). */
  preco: string;
  /** Reajuste em percentual, pode ser negativo ("5", "-3,5"). */
  reajuste: string;
  regra: RegraArredondamento;
  /** PADRAO: grava a condicao padrao e as demais derivam. TODAS: grava o valor calculado em cada condicao. */
  aplicarEm: AplicarEmLote;
}

export const FORM_LOTE_INICIAL: FormLote = { tipo: 'COPIAR_SUGERIDO', lucro: '', preco: '', reajuste: '', regra: 'CINCO', aplicarEm: 'PADRAO' };

/** Padrao do "Aplicar em" ao escolher a acao: MESMO_PRECO grava o mesmo valor em todas; as demais, a condicao padrao (as outras derivam). */
export function aplicarEmPadrao(tipo: TipoAcaoLote): AplicarEmLote {
  return tipo === 'MESMO_PRECO' ? 'TODAS' : 'PADRAO';
}

export const ROTULO_REGRA: Record<RegraArredondamento, string> = {
  CINCO: 'Arredondar para 0,05',
  DEZ: 'Arredondar para 0,10',
  TERMINAR_90: 'Terminar em ,90',
};

export type ResultadoAcao = { ok: true; acao: AcaoLoteDto } | { ok: false; erro: string };

/**
 * Reajuste digitado (-90 a 100) -> fracao com sinal da API. O sinal sai antes da conversao (`percentualParaTaxa` so aceita 0 a 100).
 * A faixa e checada aqui so para mensagem; quem valida de verdade e a API.
 */
export function reajusteParaFracao(texto: string): string | null {
  const t = texto.trim();
  const negativo = t.startsWith('-');
  const fracao = percentualParaTaxa(negativo ? t.slice(1) : t);
  if (fracao === null) return null;
  if (negativo) {
    if (new Decimal(fracao).gt('0.9')) return null; // desconto acima de 90% (-90 e o limite)
    return fracao === '0' ? '0' : `-${fracao}`;
  }
  return fracao;
}

export function acaoDoFormulario(f: FormLote): ResultadoAcao {
  switch (f.tipo) {
    case 'LUCRO': {
      const fracao = percentualParaTaxa(f.lucro);
      return fracao === null ? { ok: false, erro: 'Lucro desejado: percentual inválido (0 a 100, até 6 casas).' } : { ok: true, acao: { tipo: 'LUCRO', lucro: fracao } };
    }
    case 'MESMO_PRECO': {
      const preco = decimalDigitado(f.preco);
      return preco === null ? { ok: false, erro: 'Informe o preço, como 25,00.' } : { ok: true, acao: { tipo: 'MESMO_PRECO', preco } };
    }
    case 'REAJUSTE': {
      const pct = reajusteParaFracao(f.reajuste);
      return pct === null ? { ok: false, erro: 'Reajuste: informe de -90 a 100 (até 6 casas, ex.: 5 ou -3,5).' } : { ok: true, acao: { tipo: 'REAJUSTE', pct } };
    }
    case 'ARREDONDAR':
      return { ok: true, acao: { tipo: 'ARREDONDAR', regra: f.regra } };
    case 'COPIAR_SUGERIDO':
      return { ok: true, acao: { tipo: 'COPIAR_SUGERIDO' } };
  }
}

/** Identifica uma previa: se a acao, o escopo ou o "Aplicar em" mudarem, a previa anterior nao vale mais para aplicar. */
export function assinaturaPrevia(escopo: EscopoLoteDto, acao: AcaoLoteDto, aplicarEm: AplicarEmLote): string {
  return JSON.stringify({ escopo, acao, aplicarEm });
}
